import React, { useState } from "react";
import { Button, Form, Spinner } from "react-bootstrap";
import DatePicker from "react-date-picker";
import { toast } from "react-toastify";
import ElectricityApartmentAgreementsService, {
  ApartmentAgreement,
} from "services/electricity/electricity-apartment-agreements.service";
import { ReactComponent as CalendarIcon } from "../../../assets/icons/calendar.svg";
import { dateToIso, isoToDate, formatDate } from "../../../common/dateUtil";

interface Props {
  apartment: ApartmentAgreement;
  parentDateEnd?: string;
  onSaved: () => void;
  onCancel: () => void;
}

const todayIso = (): string => new Date().toISOString().slice(0, 10);

export default function EndApartmentForm({ apartment, parentDateEnd, onSaved, onCancel }: Props) {
  const today = todayIso();
  const minDateEnd = apartment.startDate;
  const defaultDateEnd = apartment.startDate > today ? apartment.startDate : today;
  const [dateEnd, setDateEnd] = useState(defaultDateEnd);
  const [saving, setSaving] = useState(false);
  const [validated, setValidated] = useState(false);

  const isValid =
    !!dateEnd &&
    dateEnd >= minDateEnd &&
    (!parentDateEnd || dateEnd <= parentDateEnd);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidated(true);
    if (!isValid) {
      toast.error("Lõpukuupäev ei saa olla varasem kui " + formatDate(minDateEnd));
      return;
    }
    setSaving(true);
    try {
      await ElectricityApartmentAgreementsService.setEndDate(apartment.id, { endDate: dateEnd });
      toast.success("Korteri lõpukuupäev määratud");
      onSaved();
    } catch {
      toast.error("Lõpukuupäeva määramine ebaõnnestus");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Form onSubmit={submit} className="border rounded p-3 mb-3 bg-light" noValidate validated={validated}>
      <h5>Lõpeta korteri leping</h5>
      <p className="small">
        <i className='icon__info icon__info--masked bg-dark mr-2 mb-n1'></i>
        Süsteem lõpetab lepingu valitud kuupäeval. Kuupäev ei saa olla varasem kui korteri alguskuupäev ({formatDate(minDateEnd)}).
      </p>

      <div className="row">
        <div className="col-12 col-md-6">
          <Form.Label className="mb-0 align-self-start m-1 text-center">
            <span className="text-nowrap">Lõpp*</span>
          </Form.Label>
          <DatePicker
            className={`d-block ${!dateEnd && validated ? ' invalid is-invalid' : ''}`}
            value={isoToDate(dateEnd)}
            locale='et'
            clearIcon={null}
            calendarIcon={<CalendarIcon />}
            format='dd.MM.yyyy'
            minDate={isoToDate(minDateEnd)}
            maxDate={isoToDate(parentDateEnd)}
            onChange={(e: any) => setDateEnd(dateToIso(e))}
          />
          <Form.Control.Feedback className="text-right" type="invalid">Kohustuslik väli</Form.Control.Feedback>
        </div>
      </div>

      <div className="d-flex mt-4">
        <Button type="button" variant="secondary" onClick={onCancel} disabled={saving}>
          Tühista
        </Button>
        <Button type="submit" variant="primary" disabled={saving || !isValid} className="ml-2">
          {saving ? <Spinner size="sm" animation="border" /> : "Salvesta"}
        </Button>
      </div>
    </Form>
  );
}
